import { useCurrentUserStore } from '@infuse/iam/user/store';

export type PermissionCheckType = 'any' | 'all';

export function hasRequiredPermissions(
  permissionIds: string[],
  check: PermissionCheckType = 'all',
  useSubscriptionId: boolean
): boolean {
  const currentUserStore = useCurrentUserStore();

  if (!currentUserStore.enabled) {
    return true;
  }

  if (!permissionIds.length) {
    return true;
  }

  if (check === 'any') {
    return permissionIds.some((id) =>
      currentUserStore.hasPermission(id, useSubscriptionId ? currentUserStore.mainSubscriptionId : undefined)
    );
  }
  return permissionIds.every((id) =>
    currentUserStore.hasPermission(id, useSubscriptionId ? currentUserStore.mainSubscriptionId : undefined)
  );
}

export function isPermissionAllowed(meta: Record<string, any>): boolean {
  const { permissionIds, permissionCheck, useSubscriptionId } = getPermissionMeta(meta);
  return hasRequiredPermissions(permissionIds, permissionCheck, useSubscriptionId);
}

export function isApplicationAllowed(): boolean {
  const { enabled, permissions, user } = useCurrentUserStore();

  if (!enabled) {
    return true;
  }

  // These lib files do not like the Vue import.meta.env, but it does in fact exist
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  const { VITE_UTILS_APP_PERMISSION_PREFIX = '', VITE_DISALLOWED_ROLES = '' } = import.meta.env;

  try {
    const disallowedRoles = VITE_DISALLOWED_ROLES.split(',');
    if (user?.roleLabels.some((role) => disallowedRoles.includes(role.label))) {
      // Do not let the user in when the specific role is excluded from entering
      return false;
    }

    // Check if the user has a permission for this specific application, for example for Infuse CPMS, user should have a permission starting with cpms.
    return permissions.some((permission) => permission.permissionId.startsWith(VITE_UTILS_APP_PERMISSION_PREFIX));
  } catch (err) {
    // catch errors and return "not allowed" - without the try/catch block above a broken login would just Error and end up on a static white screen
    console.error(err);
    return false;
  }
}

export function isUserDeactivated(): boolean {
  const { enabled, user } = useCurrentUserStore();
  if (!enabled) {
    return false;
  }
  if (!user) {
    return false;
  }
  return !user.active;
}

function getPermissionMeta(meta: Record<string, any>): {
  permissionIds: string[];
  permissionCheck: PermissionCheckType;
  useSubscriptionId?: boolean;
} {
  return {
    permissionIds: meta?.permissionIds || [],
    permissionCheck: meta?.permissionCheck || 'any',
    useSubscriptionId: meta?.useSubscriptionId || false
  };
}
